import { useCallback } from "react";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { useCentrosCusto } from "layouts/pages/loja/financeiro/dashboard/hooks/useCentrosCusto";
import { useContasPagar } from "layouts/pages/loja/financeiro/dashboard/hooks/useContasPagar";
import { useFornecedoresFinanceiro } from "layouts/pages/loja/financeiro/dashboard/hooks/useFornecedoresFinanceiro";
import { useContasFinanceirasFinanceiro } from "layouts/pages/loja/financeiro/dashboard/hooks/useContasFinanceirasFinanceiro";
import { useBaixaTitulo } from "layouts/pages/loja/financeiro/dashboard/hooks/useBaixaTitulo";
import { useCancelamentoTitulo } from "layouts/pages/loja/financeiro/dashboard/hooks/useCancelamentoTitulo";
import { useEditarTituloPagar } from "layouts/pages/loja/financeiro/dashboard/hooks/useEditarTituloPagar";
import ContasPagarSection from "layouts/pages/loja/financeiro/dashboard/components/ContasPagarSection";
import BaixaTituloDialog from "layouts/pages/loja/financeiro/dashboard/components/BaixaTituloDialog";
import CancelamentoTituloDialog from "layouts/pages/loja/financeiro/dashboard/components/CancelamentoTituloDialog";
import NovaContaPagarDialog from "layouts/pages/loja/financeiro/dashboard/components/NovaContaPagarDialog";

type Props = {
  eventoId: number;
};

export default function DespesasEventoRelatorio({ eventoId }: Props) {
  const contasPagar = useContasPagar({ eventoId });
  const centros = useCentrosCusto({ ativo: true, somenteEventosAtivos: true });
  const fornecedoresFinanceiro = useFornecedoresFinanceiro();
  const contasFinanceirasFinanceiro = useContasFinanceirasFinanceiro();

  const handleMutacaoSucesso = useCallback(() => {
    contasPagar.refetch();
    fornecedoresFinanceiro.refetch();
  }, [contasPagar, fornecedoresFinanceiro]);

  const editarTitulo = useEditarTituloPagar({ onSuccess: handleMutacaoSucesso });
  const baixaTitulo = useBaixaTitulo({
    onSuccess: handleMutacaoSucesso,
    contasFinanceiras: contasFinanceirasFinanceiro.data,
  });
  const cancelamentoTitulo = useCancelamentoTitulo({
    onSuccess: handleMutacaoSucesso,
  });

  const semPermissao =
    contasPagar.error === "Sem permissão para listar contas a pagar.";

  return (
    <>
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          py: 2,
          px: { xs: 2, sm: 8 },
          my: 2,
        }}
      >
        <Typography variant="h4" fontWeight="bold" color="dark">
          Despesas
        </Typography>

        <Divider sx={{ my: 2 }} />

        {semPermissao ? (
          <Typography variant="subtitle2" textAlign="center" color="text.secondary">
            Sem permissão para visualizar despesas financeiras do evento.
          </Typography>
        ) : (
          <>
            {editarTitulo.feedback ? (
              <Alert severity={editarTitulo.feedback.severity} sx={{ mb: 2 }}>
                {editarTitulo.feedback.message}
              </Alert>
            ) : null}

            <ContasPagarSection
              contasPagar={contasPagar}
              centros={centros}
              fornecedores={fornecedoresFinanceiro}
              ocultarFiltros
              ocultarCards
              tabelaVariant="evento"
              tabelaEmbedded
              onBaixarTitulo={
                baixaTitulo.podeBaixaManual
                  ? (titulo) =>
                      baixaTitulo.abrirBaixaPagar(
                        titulo,
                        contasFinanceirasFinanceiro.data,
                      )
                  : undefined
              }
              onCancelarTitulo={
                cancelamentoTitulo.podeCancelarTitulo
                  ? cancelamentoTitulo.abrirCancelamentoPagar
                  : undefined
              }
              onEditarTitulo={
                editarTitulo.podeEditar ? editarTitulo.abrir : undefined
              }
            />
          </>
        )}
      </Card>

      <NovaContaPagarDialog
        open={editarTitulo.open}
        form={editarTitulo.form}
        setForm={editarTitulo.setForm}
        loading={editarTitulo.loading}
        error={editarTitulo.error}
        centros={centros.data}
        fornecedores={fornecedoresFinanceiro.data}
        onRefetchFornecedores={fornecedoresFinanceiro.refetch}
        onClose={editarTitulo.fechar}
        onConfirm={() => void editarTitulo.confirmar()}
        variant="edit"
      />

      <BaixaTituloDialog
        dialog={baixaTitulo.dialog}
        form={baixaTitulo.form}
        setForm={baixaTitulo.setForm}
        contasFinanceiras={contasFinanceirasFinanceiro.data}
        contasLoading={contasFinanceirasFinanceiro.loading}
        contasError={contasFinanceirasFinanceiro.error}
        loading={baixaTitulo.loading}
        error={baixaTitulo.error}
        onClose={baixaTitulo.fecharDialog}
        onConfirm={baixaTitulo.confirmarBaixa}
      />

      <CancelamentoTituloDialog
        dialog={cancelamentoTitulo.dialog}
        form={cancelamentoTitulo.form}
        setForm={cancelamentoTitulo.setForm}
        loading={cancelamentoTitulo.loading}
        error={cancelamentoTitulo.error}
        onClose={cancelamentoTitulo.fecharDialog}
        onConfirm={cancelamentoTitulo.confirmarCancelamento}
      />
    </>
  );
}
